import React from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { IGutterProps } from '../../fundamentals/spaces/space';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { useTheme } from 'styled-components';
import { getHexOpacity } from '../../common/utils';

export const skeletonColours = ({ base }: IColoursCollection) => ({
  skeletonColours: {
    background: `${base.black.colour}${getHexOpacity(7)}`,
    text: `${base.black.colour}${getHexOpacity(3)}`,
    light: {
      background: `${base.white.colour}${getHexOpacity(30)}`,
      text: `${base.white.colour}${getHexOpacity(20)}`
    }
  }
});

export const LOADER_TYPES = {
  TRIO: 'trio',
  TRIO_COMPACT: 'trioCompact',
  TRIO_COMPACT_SLIM: 'trioCompactSlim',
  USER: 'user',
  AVATAR: 'avatar',
  INDEX: 'index',
  INDEX_NO_FLAG: 'indexNoFlag',
  LAST_PRICE: 'lastPrice',
  DOUBLE_LINE: 'doubleLine',
  SINGLE_LINE: 'singleLine'
} as const;

export type LoaderType = (typeof LOADER_TYPES)[keyof typeof LOADER_TYPES];

export interface ISkeletonProps extends IGutterProps {
  loaderType?: LoaderType;
  skeletonWidth?: number;
  light?: boolean;
}

export interface ISkeletonBaseProps extends Omit<IContentLoaderProps, 'height' | 'width'> {
  skeletonWidth: number;
  light: boolean;
}

interface ISkeletonContainerProps extends Pick<IContentLoaderProps, 'height' | 'width' | 'children'> {
  light: boolean;
}

const SkeletonContainer: React.FC<ISkeletonContainerProps> = ({ light, children, ...rest }) => {
  const theme = useTheme();
  return (
    <ContentLoader
      backgroundColor={light ? theme.skeletonColours.light.background : theme.skeletonColours.background}
      foregroundColor={light ? theme.skeletonColours.light.text : theme.skeletonColours.text}
      speed={2}
      {...rest}
    >
      {children}
    </ContentLoader>
  );
};

const SKELETON_WIDTH = 284;
const SKELETON_HEIGHT = 40;
const SKELETON_GAP = 8;
const SMALL_CIRCLE_SIZE = SKELETON_GAP * 2;
const LARGE_CIRCLE_RADIUS = SKELETON_HEIGHT / 2;
const COMPACT_CIRCLE_RADIUS = SKELETON_HEIGHT / 4;
const RECT_START = SKELETON_HEIGHT + SKELETON_GAP;
const RECT_HEIGHT = (SKELETON_HEIGHT - SKELETON_GAP) / 2;

export const capitaliseFirstLetter = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const AvatarCircle: React.FC = () => <circle cx={LARGE_CIRCLE_RADIUS} cy={LARGE_CIRCLE_RADIUS} r={LARGE_CIRCLE_RADIUS} />;
const AvatarCircleCompact: React.FC = () => <circle cx={COMPACT_CIRCLE_RADIUS} cy={COMPACT_CIRCLE_RADIUS} r={COMPACT_CIRCLE_RADIUS} />;
const SingleLine: React.FC<{ x?: number; width?: number; y?: number }> = ({ width, ...rest }) => (
  <rect rx={SKELETON_GAP} ry={SKELETON_GAP} width={width} height={RECT_HEIGHT} {...rest} />
);

export const SkeletonTrio: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
    <AvatarCircle />
    <SingleLine x={RECT_START} width={skeletonWidth - SKELETON_HEIGHT - SMALL_CIRCLE_SIZE - SMALL_CIRCLE_SIZE} />
    <circle cx={skeletonWidth - SKELETON_GAP} cy={SKELETON_GAP} r={SKELETON_GAP} />
    <SingleLine x={RECT_START} y={RECT_HEIGHT + SKELETON_GAP} width={skeletonWidth - SKELETON_GAP - SKELETON_HEIGHT} />
  </SkeletonContainer>
);

export const SkeletonTrioCompact: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
    <AvatarCircleCompact />
    <SingleLine x={RECT_START} width={skeletonWidth - SKELETON_HEIGHT - SMALL_CIRCLE_SIZE - SMALL_CIRCLE_SIZE - 1} />
    <circle cx={skeletonWidth - SKELETON_GAP} cy={SKELETON_GAP} r={SKELETON_GAP} />
    <SingleLine x={RECT_START} y={RECT_HEIGHT + SKELETON_GAP} width={skeletonWidth - SKELETON_GAP - SKELETON_HEIGHT} />
  </SkeletonContainer>
);

export const SkeletonTrioCompactSlim: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT / 2} {...rest}>
    <AvatarCircleCompact />
    <SingleLine x={RECT_START} width={skeletonWidth - SKELETON_HEIGHT - SMALL_CIRCLE_SIZE - SMALL_CIRCLE_SIZE - 2} />
    <circle cx={skeletonWidth - SKELETON_GAP} cy={SKELETON_GAP} r={SKELETON_GAP} />
  </SkeletonContainer>
);

export const SkeletonUser: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
    <AvatarCircle />
    <SingleLine x={RECT_START} width={skeletonWidth - SKELETON_HEIGHT - SKELETON_GAP} />
    <SingleLine x={RECT_START} y={RECT_HEIGHT + SKELETON_GAP} width={skeletonWidth - SKELETON_GAP - SKELETON_HEIGHT} />
  </SkeletonContainer>
);

export const SkeletonAvatar: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={SKELETON_HEIGHT} height={SKELETON_HEIGHT} {...rest}>
    <AvatarCircle />
  </SkeletonContainer>
);

const SkeletonIndexBase: React.FC<ISkeletonBaseProps & { showFlag: boolean }> = ({ skeletonWidth, showFlag, ...rest }) => {
  const rectWidth = skeletonWidth - RECT_START;
  const secondRowSegmentWidth = ((showFlag ? rectWidth : skeletonWidth) - SKELETON_GAP * 2) / 3;
  const start = showFlag ? RECT_START : 0;
  const fullWidth = showFlag ? rectWidth : skeletonWidth;
  return (
    <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
      {showFlag && <AvatarCircle />}
      <SingleLine x={start} width={fullWidth / 2 - SKELETON_GAP / 2} />
      <SingleLine x={start + fullWidth / 2 + SKELETON_GAP / 2} width={fullWidth / 6 - SKELETON_GAP} />
      {[0, 1, 2].map((i) => (
        <SingleLine key={i} x={start + i * secondRowSegmentWidth + i * SKELETON_GAP} y={RECT_HEIGHT + SKELETON_GAP} width={secondRowSegmentWidth} />
      ))}
    </SkeletonContainer>
  );
};

export const SkeletonIndex: React.FC<ISkeletonBaseProps> = ({ ...rest }) => <SkeletonIndexBase showFlag {...rest} />;

export const SkeletonIndexNoFlag: React.FC<ISkeletonBaseProps> = ({ ...rest }) => <SkeletonIndexBase showFlag={false} {...rest} />;

const SkeletonLastPrice: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => {
  const secondRowSegmentWidth = (skeletonWidth - SKELETON_GAP * 4) / 2;
  return (
    <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
      <SingleLine width={skeletonWidth} />
      <circle cx={SKELETON_GAP} cy={RECT_HEIGHT + SKELETON_GAP * 2} r={SKELETON_GAP} />
      {[0, 1].map((i) => (
        <SingleLine key={i} x={i * secondRowSegmentWidth + i * SKELETON_GAP + SKELETON_GAP * 3} y={RECT_HEIGHT + SKELETON_GAP} width={secondRowSegmentWidth} />
      ))}
    </SkeletonContainer>
  );
};

const SkeletonDoubleLine: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={SKELETON_HEIGHT} {...rest}>
    <SingleLine width={skeletonWidth} />
    <SingleLine width={skeletonWidth} y={RECT_HEIGHT + SKELETON_GAP} />
  </SkeletonContainer>
);

const SkeletonSingleLine: React.FC<ISkeletonBaseProps> = ({ skeletonWidth, ...rest }) => (
  <SkeletonContainer width={skeletonWidth} height={RECT_HEIGHT} {...rest}>
    <SingleLine width={skeletonWidth} />
  </SkeletonContainer>
);

const Skeletons: Record<LoaderType, React.FC<ISkeletonBaseProps>> = {
  trio: SkeletonTrio,
  trioCompact: SkeletonTrioCompact,
  trioCompactSlim: SkeletonTrioCompactSlim,
  user: SkeletonUser,
  avatar: SkeletonAvatar,
  index: SkeletonIndex,
  indexNoFlag: SkeletonIndexNoFlag,
  lastPrice: SkeletonLastPrice,
  doubleLine: SkeletonDoubleLine,
  singleLine: SkeletonSingleLine
};

export const Skeleton: React.FC<ISkeletonProps> = ({ loaderType = 'trio', skeletonWidth = SKELETON_WIDTH, light = false, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Skeleton');
  const SelectedSkeleton = Skeletons[loaderType];
  return (
    <GapContainer {...rest} {...generateTestId()}>
      <SelectedSkeleton {...rest} {...generateTestId(capitaliseFirstLetter(loaderType))} skeletonWidth={skeletonWidth} light={light} />
    </GapContainer>
  );
};
